import React, { useDeferredValue, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
    FiActivity,
    FiAlertTriangle,
    FiArrowRight,
    FiChevronLeft,
    FiCheckCircle,
    FiClock,
    FiEdit3,
    FiExternalLink,
    FiFileText,
    FiFilter,
    FiGlobe,
    FiLayers,
    FiLink2,
    FiRefreshCw,
    FiSave,
    FiSearch,
    FiShield,
    FiTarget,
    FiTrendingUp,
    FiType,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import { config } from "config/config";
import { showToast } from "components/shared/Toast";

const TODAY_LABEL = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
}).format(new Date());

const SEO_PAGE_INVENTORY = [
    {
        id: "home",
        page: "Home",
        route: "/",
        type: "Core",
        section: "Brand",
        indexing: "Indexed",
        meta: "Optimized",
        schema: "Organization + FAQ",
        score: 94,
        status: "Healthy",
        nextStep: "Refresh FAQ schema after the summer offers update.",
        previewPath: "/",
    },
    {
        id: "search",
        page: "Search",
        route: "/search",
        type: "Utility",
        section: "Discovery",
        indexing: "Noindex",
        meta: "Review",
        schema: "SearchAction",
        score: 72,
        status: "Monitor",
        nextStep: "Confirm noindex and canonical handling for filtered results.",
        previewPath: "/search",
    },
    {
        id: "trek-listing",
        page: "Trek Listing",
        route: "/trek",
        type: "Collection",
        section: "Trek",
        indexing: "Indexed",
        meta: "Strong",
        schema: "BreadcrumbList",
        score: 89,
        status: "Healthy",
        nextStep: "Add seasonal intro copy for monsoon and winter trekking terms.",
        previewPath: "/trek",
    },
    {
        id: "trek-detail",
        page: "Trek Detail Template",
        route: "/trek/[slug]",
        type: "Template",
        section: "Trek",
        indexing: "Indexed",
        meta: "Good",
        schema: "Article + BreadcrumbList",
        score: 86,
        status: "Healthy",
        nextStep: "Enforce unique intros and FAQs for every trek detail page.",
        previewPath: null,
    },
    {
        id: "tour-detail",
        page: "Tour Detail Template",
        route: "/tour/[slug]",
        type: "Template",
        section: "Tour",
        indexing: "Partial",
        meta: "Needs Work",
        schema: "Inconsistent",
        score: 74,
        status: "Needs Review",
        nextStep: "Standardize title structure and resolve schema validation warnings.",
        previewPath: null,
    },
    {
        id: "travel-detail",
        page: "Travel Detail Template",
        route: "/travel/[slug]",
        type: "Template",
        section: "Travel",
        indexing: "Indexed",
        meta: "Good",
        schema: "Good",
        score: 84,
        status: "Healthy",
        nextStep: "Expand internal links to nearby travel guides and itineraries.",
        previewPath: null,
    },
    {
        id: "upcoming-events",
        page: "Upcoming Events",
        route: "/upcoming-events",
        type: "Campaign",
        section: "Events",
        indexing: "Pending",
        meta: "Draft",
        schema: "Planned",
        score: 61,
        status: "In Progress",
        nextStep: "Launch event schema, speaker fields, and ticketing metadata.",
        previewPath: null,
    },
    {
        id: "readers-corner",
        page: "Reader's Corner",
        route: "/readers-corner",
        type: "Editorial",
        section: "Content",
        indexing: "Planned",
        meta: "Draft",
        schema: "Planned",
        score: 52,
        status: "Planned",
        nextStep: "Define taxonomy, article templates, and article schema defaults.",
        previewPath: null,
    },
    {
        id: "archeological-sites",
        page: "Archeological Sites",
        route: "/archeological-sites",
        type: "Category",
        section: "Expansion",
        indexing: "Backlog",
        meta: "Missing",
        schema: "Missing",
        score: 38,
        status: "Backlog",
        nextStep: "Create the landing page before opening it for indexing.",
        previewPath: null,
    },
];

const ON_PAGE_SEO_ROWS = [
    {
        id: "onpage-home",
        page: "Home",
        type: "Core",
        keyword: "trek tour travel maharashtra",
        titleTag: "58 / 60",
        metaDescription: "149 / 155",
        headingStatus: "Single H1 + supporting H2s",
        internalLinks: "18 contextual links",
        mediaCoverage: "92% image alt coverage",
        schema: "Organization + FAQ",
        score: 93,
        status: "Strong",
    },
    {
        id: "onpage-trek-listing",
        page: "Trek Listing",
        type: "Collection",
        keyword: "best treks in maharashtra",
        titleTag: "56 / 60",
        metaDescription: "151 / 155",
        headingStatus: "Clear listing hierarchy",
        internalLinks: "24 destination links",
        mediaCoverage: "89% alt coverage",
        schema: "BreadcrumbList",
        score: 90,
        status: "Strong",
    },
    {
        id: "onpage-trek-detail",
        page: "Trek Detail Template",
        type: "Template",
        keyword: "trek near [location]",
        titleTag: "Variable by location",
        metaDescription: "Needs unique copy",
        headingStatus: "H1 okay, FAQs uneven",
        internalLinks: "9 supporting links",
        mediaCoverage: "81% alt coverage",
        schema: "Article + FAQ",
        score: 84,
        status: "Improve",
    },
    {
        id: "onpage-tour-detail",
        page: "Tour Detail Template",
        type: "Template",
        keyword: "tour package [destination]",
        titleTag: "Often too long",
        metaDescription: "Duplicate risk",
        headingStatus: "Headings need cleanup",
        internalLinks: "6 supporting links",
        mediaCoverage: "76% alt coverage",
        schema: "Breadcrumb only",
        score: 71,
        status: "Needs Review",
    },
    {
        id: "onpage-travel-detail",
        page: "Travel Detail Template",
        type: "Template",
        keyword: "travel guide [destination]",
        titleTag: "57 / 60",
        metaDescription: "144 / 155",
        headingStatus: "Strong section hierarchy",
        internalLinks: "11 guide links",
        mediaCoverage: "84% alt coverage",
        schema: "Article",
        score: 85,
        status: "Strong",
    },
    {
        id: "onpage-upcoming-events",
        page: "Upcoming Events",
        type: "Campaign",
        keyword: "upcoming travel events",
        titleTag: "Draft",
        metaDescription: "Draft",
        headingStatus: "Template not finalized",
        internalLinks: "Planned",
        mediaCoverage: "Planned",
        schema: "Event schema pending",
        score: 60,
        status: "In Progress",
    },
    {
        id: "onpage-readers-corner",
        page: "Reader's Corner",
        type: "Editorial",
        keyword: "travel stories and guides",
        titleTag: "Not started",
        metaDescription: "Not started",
        headingStatus: "Editorial layout pending",
        internalLinks: "Not mapped",
        mediaCoverage: "Not mapped",
        schema: "Article schema planned",
        score: 49,
        status: "Planned",
    },
];

const OFF_PAGE_SEO_ROWS = [
    {
        id: "offpage-brand-citations",
        campaign: "Brand citations refresh",
        focusPage: "Home",
        channel: "Directories",
        authority: "DA 54 avg",
        liveLinks: 12,
        referralSessions: 184,
        status: "Active",
        nextAction: "Update business descriptions and destination tags.",
    },
    {
        id: "offpage-trek-outreach",
        campaign: "Trek guide outreach",
        focusPage: "Trek Listing",
        channel: "Guest features",
        authority: "DA 48 avg",
        liveLinks: 7,
        referralSessions: 126,
        status: "Scaling",
        nextAction: "Pitch monsoon trek roundup to outdoor blogs.",
    },
    {
        id: "offpage-tour-partners",
        campaign: "Tour partner backlinks",
        focusPage: "Tour Detail Template",
        channel: "Partner pages",
        authority: "DA 42 avg",
        liveLinks: 4,
        referralSessions: 73,
        status: "Watch",
        nextAction: "Request branded anchor updates from partner operators.",
    },
    {
        id: "offpage-event-calendars",
        campaign: "Event calendar syndication",
        focusPage: "Upcoming Events",
        channel: "Calendars",
        authority: "DA 37 avg",
        liveLinks: 3,
        referralSessions: 41,
        status: "In Progress",
        nextAction: "Finalize launch page and submit to event portals.",
    },
    {
        id: "offpage-youtube-embeds",
        campaign: "Video embed distribution",
        focusPage: "Travel Detail Template",
        channel: "YouTube + blogs",
        authority: "DA 45 avg",
        liveLinks: 5,
        referralSessions: 89,
        status: "Active",
        nextAction: "Bundle travel guides with itinerary videos and creator mentions.",
    },
    {
        id: "offpage-editorial-pr",
        campaign: "Editorial PR plan",
        focusPage: "Reader's Corner",
        channel: "Digital PR",
        authority: "Target DA 60+",
        liveLinks: 0,
        referralSessions: 0,
        status: "Planned",
        nextAction: "Prepare launch story angles before section goes live.",
    },
];

const TECHNICAL_SEO_ROWS = [
    {
        id: "tech-sitemap",
        area: "XML sitemap freshness",
        metric: "Sitemap regenerated after content publish",
        current: "Synced 2 hours ago",
        target: "Under 24 hours",
        impact: "Keeps new detail pages discoverable faster.",
        status: "Stable",
        owner: "Platform",
    },
    {
        id: "tech-robots",
        area: "Robots and noindex rules",
        metric: "Search and utility pages excluded cleanly",
        current: "Search page set to noindex",
        target: "All utility routes controlled",
        impact: "Prevents low-value pages from polluting crawl budget.",
        status: "Healthy",
        owner: "Platform",
    },
    {
        id: "tech-cwv",
        area: "Core Web Vitals",
        metric: "Mobile LCP / CLS / INP",
        current: "LCP 2.8s, CLS 0.03, INP 180ms",
        target: "LCP under 2.5s",
        impact: "Improves mobile rankings and conversion quality.",
        status: "Needs Review",
        owner: "Frontend",
    },
    {
        id: "tech-canonical",
        area: "Canonical consistency",
        metric: "Self canonicals across templates",
        current: "Trek and travel stable, tour needs review",
        target: "All templates aligned",
        impact: "Reduces duplicate-url competition in SERPs.",
        status: "Monitor",
        owner: "SEO Desk",
    },
    {
        id: "tech-schema",
        area: "Structured data validation",
        metric: "Warnings in Search Console rich results",
        current: "2 warnings on tour template",
        target: "0 warnings",
        impact: "Supports rich results and cleaner crawl interpretation.",
        status: "In Progress",
        owner: "Content + Dev",
    },
    {
        id: "tech-links",
        area: "Broken link watchlist",
        metric: "Broken internal references",
        current: "6 URLs flagged in content library",
        target: "0 broken internal links",
        impact: "Protects crawl quality and user trust.",
        status: "Needs Review",
        owner: "Editorial",
    },
    {
        id: "tech-images",
        area: "Image optimization",
        metric: "Compressed hero and gallery assets",
        current: "81% optimized",
        target: "95% optimized",
        impact: "Improves LCP on destination detail pages.",
        status: "Monitor",
        owner: "Media",
    },
    {
        id: "tech-redirects",
        area: "Redirect coverage",
        metric: "Legacy destination slugs redirected",
        current: "High-priority redirects mapped",
        target: "Full legacy coverage",
        impact: "Preserves equity from older travel URLs.",
        status: "Healthy",
        owner: "Platform",
    },
];

const SEO_ALERTS = [
    {
        id: "alert-tour-schema",
        priority: "High",
        title: "Tour template is missing stable schema output",
        detail: "Rich result warnings are concentrated on tour detail pages and should be fixed before scaling more tour content.",
    },
    {
        id: "alert-search",
        priority: "Medium",
        title: "Search route needs periodic noindex verification",
        detail: "Query-based pages should remain excluded from indexing after every navigation and template update.",
    },
    {
        id: "alert-events",
        priority: "Medium",
        title: "Upcoming Events launch plan needs SEO defaults",
        detail: "Meta templates, event schema, and internal links should be prepared before the page is promoted.",
    },
    {
        id: "alert-links",
        priority: "Low",
        title: "Internal link gaps remain on long-form destination pages",
        detail: "Travel and trek detail pages still need stronger cross-linking to related itineraries and supporting guides.",
    },
];

const ACTION_QUEUE = [
    {
        id: "action-tour-meta",
        title: "Normalize tour title and description patterns",
        owner: "Content Team",
        due: "April 10",
        status: "In Progress",
    },
    {
        id: "action-schema",
        title: "Resolve schema warnings on tour and event templates",
        owner: "Frontend",
        due: "April 12",
        status: "Queued",
    },
    {
        id: "action-outreach",
        title: "Launch second round of trek outreach partnerships",
        owner: "Outreach",
        due: "April 14",
        status: "Ready",
    },
    {
        id: "action-editorial",
        title: "Define Reader's Corner taxonomy and article metadata",
        owner: "Editorial",
        due: "April 18",
        status: "Planned",
    },
];

const ON_PAGE_HIGHLIGHTS = [
    { id: "titles", label: "Title tags on target", value: "4 / 7", note: "Template-driven pages still need tighter title rules." },
    { id: "descriptions", label: "Meta descriptions ready", value: "4 / 7", note: "Event and editorial sections are still in draft state." },
    { id: "schema", label: "Schema coverage", value: "71%", note: "Tour and upcoming event templates are the main gap." },
    { id: "links", label: "Internal linking strength", value: "Moderate", note: "Add more supporting links inside long-form location pages." },
];

const OFF_PAGE_HIGHLIGHTS = [
    { id: "domains", label: "Referring domains in play", value: "31", note: "Brand, trek, and travel campaigns are the strongest sources." },
    { id: "links", label: "Live backlinks tracked", value: "31", note: "Most links are from partner directories and niche blogs." },
    { id: "traffic", label: "Referral sessions", value: "513", note: "Trek listings and home page attract the largest share." },
    { id: "pipeline", label: "Pipeline status", value: "2 active / 2 scaling", note: "Reader's Corner PR stays in pre-launch mode for now." },
];

const TECHNICAL_HIGHLIGHTS = [
    { id: "crawl", label: "Crawl governance", value: "Strong", note: "Robots, noindex, and sitemap refresh are in a good place." },
    { id: "cwv", label: "Mobile performance", value: "Needs work", note: "Hero images and detail layouts still affect LCP." },
    { id: "links", label: "Broken link watch", value: "6 issues", note: "Most of the flagged URLs are content-level references." },
    { id: "readiness", label: "Launch readiness", value: "78%", note: "Templates are close, but event and editorial sections need defaults." },
];

const FOCUS_AREAS = [
    {
        id: "pages",
        title: "All Pages",
        description: "Track every live, planned, and template-driven route from one inventory.",
        stat: `${SEO_PAGE_INVENTORY.length} tracked routes`,
        icon: <FiLayers />,
    },
    {
        id: "on-page",
        title: "On-Page SEO",
        description: "Review title tags, meta descriptions, headings, schema, and linking hygiene.",
        stat: `${ON_PAGE_SEO_ROWS.length} optimization rows`,
        icon: <FiSearch />,
    },
    {
        id: "off-page",
        title: "Off-Page SEO",
        description: "Monitor backlinks, outreach, PR, partner mentions, and referral growth.",
        stat: `${OFF_PAGE_SEO_ROWS.length} live campaigns`,
        icon: <FiLink2 />,
    },
    {
        id: "technical",
        title: "Technical SEO",
        description: "Stay ahead of crawl, schema, speed, redirect, and indexation issues.",
        stat: `${TECHNICAL_SEO_ROWS.length} technical checks`,
        icon: <FiShield />,
    },
];

const TAB_ITEMS = [
    { key: "overview", label: "Overview" },
    { key: "pages", label: "All Pages" },
    { key: "on-page", label: "On-Page SEO" },
    { key: "off-page", label: "Off-Page SEO" },
    { key: "technical", label: "Technical SEO" },
];

const normalizeValue = (value = "") => String(value).toLowerCase().trim();

const matchesSearch = (query, values = []) =>
    !query ||
    values.some((value) => normalizeValue(value).includes(query));

const averageScore = (rows = []) => {
    if (!rows.length) {
        return 0;
    }

    const total = rows.reduce((sum, row) => sum + (row.score || 0), 0);
    return Math.round(total / rows.length);
};

const getTone = (label = "") => {
    const value = normalizeValue(label);

    if (["healthy", "indexed", "optimized", "strong", "good", "stable", "active", "ready", "synced"].includes(value)) {
        return "success";
    }

    if (["backlog", "missing", "critical"].includes(value)) {
        return "danger";
    }

    if (["in progress", "queued", "noindex", "scaling"].includes(value)) {
        return "info";
    }

    return "warning";
};

const getPriorityTone = (priority = "") => {
    const value = normalizeValue(priority);

    if (value === "high") {
        return "danger";
    }

    if (value === "medium") {
        return "warning";
    }

    return "info";
};

const getScoreTone = (score = 0) => {
    if (score >= 90) {
        return "success";
    }

    if (score >= 75) {
        return "info";
    }

    if (score >= 60) {
        return "warning";
    }

    return "danger";
};

const StatusPill = ({ children, tone }) => (
    <span className={`seoPill seoPill--${tone || getTone(children)}`}>{children}</span>
);

const ScoreBadge = ({ score }) => {
    const tone = getScoreTone(score);

    return (
        <div className="seoScore">
            <span className={`seoScore__value seoScore__value--${tone}`}>{score}</span>
            <span className="seoScore__track">
                <span className={`seoScore__fill seoScore__fill--${tone}`} style={{ width: `${score}%` }} />
            </span>
        </div>
    );
};

const MetricCard = ({ icon, label, value, detail, accent }) => (
    <article className="seoMetricCard">
        <span className={`seoMetricCard__icon seoMetricCard__icon--${accent}`}>{icon}</span>
        <span className="seoMetricCard__label">{label}</span>
        <strong className="seoMetricCard__value">{value}</strong>
        <p className="seoMetricCard__detail">{detail}</p>
    </article>
);

const DataTable = ({ columns, rows, emptyMessage }) => (
    <div className="seoTableWrap">
        <table className="seoDataTable">
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.length ? (
                    rows.map((row) => (
                        <tr key={row.id}>
                            {columns.map((column) => (
                                <td key={`${row.id}-${column.key}`}>
                                    {column.render ? column.render(row) : row[column.key]}
                                </td>
                            ))}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={columns.length}>
                            <div className="seoEmptyState">{emptyMessage}</div>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

const SEO_PAGE_DETAILS_STORAGE_KEY = "trektourtravel.admin.seo.page-details";

const SEO_PAGE_DETAILS_SEED = {
    home: {
        metaState: "Optimized",
        focusKeyword: "trek tour travel maharashtra",
        secondaryKeywords: "weekend getaways, adventure travel, curated tours",
        titleTag: "Trek Tour Travel Maharashtra | Explore Trails, Tours and Travel Guides",
        metaDescription: "Discover treks, tours, travel guides, and curated itineraries across Maharashtra with Trek Tour Travel.",
        h1Title: "Explore Maharashtra with Trek, Tour and Travel Experiences",
        canonicalUrl: `${config.clientPath}/`,
        robotsDirective: "index,follow",
        schemaType: "Organization + FAQ",
        ogTitle: "Explore Maharashtra with Trek Tour Travel",
        ogDescription: "Treks, tours, destination ideas, and travel planning in one place.",
        pageGoal: "Drive branded discovery and route users into trek, tour, and travel sections.",
        contentNotes: "Keep hero copy seasonal and update homepage FAQs when major campaigns launch.",
        internalLinkPlan: "Link to trek listing, travel detail pages, upcoming events, and reader content from hero and FAQ blocks.",
        backlinkPlan: "Use brand citations, travel directories, and homepage PR mentions to support branded search.",
        technicalNotes: "Monitor homepage hero weight and structured data validation after each design refresh.",
        nextStep: "Refresh FAQ schema after the summer offers update.",
        sitemapIncluded: true,
    },
    search: {
        metaState: "Review",
        focusKeyword: "travel site search",
        secondaryKeywords: "site search experience, filtered destination results",
        titleTag: "Search Trek Tour Travel Content",
        metaDescription: "Search destinations, guides, tours, and travel pages across the Trek Tour Travel website.",
        h1Title: "Search Destinations and Guides",
        canonicalUrl: `${config.clientPath}/search`,
        robotsDirective: "noindex,follow",
        schemaType: "SearchAction",
        ogTitle: "Search Trek Tour Travel",
        ogDescription: "Search destinations and travel guides across the website.",
        pageGoal: "Support internal discovery without competing in search results.",
        contentNotes: "No long-form copy needed; keep the page focused on search utility and clean results.",
        internalLinkPlan: "Only surface contextual result links and avoid indexable faceted states.",
        backlinkPlan: "Do not actively build backlinks to utility search pages.",
        technicalNotes: "Re-check noindex behavior whenever search UI or query params change.",
        nextStep: "Confirm noindex and canonical handling for filtered results.",
        sitemapIncluded: false,
    },
    "trek-listing": {
        metaState: "Strong",
        focusKeyword: "best treks in maharashtra",
        secondaryKeywords: "weekend treks, trekking places in maharashtra, hiking routes",
        titleTag: "Best Treks in Maharashtra | Trek Routes, Guides and Highlights",
        metaDescription: "Browse the best treks in Maharashtra with route highlights, images, and planning details.",
        h1Title: "Best Treks in Maharashtra",
        canonicalUrl: `${config.clientPath}/trek`,
        robotsDirective: "index,follow",
        schemaType: "BreadcrumbList",
        ogTitle: "Browse the Best Treks in Maharashtra",
        ogDescription: "Find trek routes, planning tips, and destination highlights across Maharashtra.",
        pageGoal: "Capture high-intent trekking queries and funnel users into detail pages.",
        contentNotes: "Keep intro copy seasonal and expand destination summaries with regional keywords.",
        internalLinkPlan: "Link to trek detail pages, upcoming events, and related travel guides from listing modules.",
        backlinkPlan: "Use outdoor blogs, regional tourism roundups, and trek communities for backlinks.",
        technicalNotes: "Maintain clean pagination, lazy-load imagery carefully, and protect LCP.",
        nextStep: "Add seasonal intro copy for monsoon and winter trekking terms.",
        sitemapIncluded: true,
    },
    "trek-detail": {
        metaState: "Good",
        focusKeyword: "trek near [location]",
        secondaryKeywords: "trek difficulty, trek itinerary, trek guide",
        titleTag: "Trek Near [Location] | Route Details, Itinerary and Highlights",
        metaDescription: "Plan your trek near [location] with route details, attractions, image gallery, and local guide information.",
        h1Title: "Trek Near [Location]",
        canonicalUrl: `${config.clientPath}/trek/[slug]`,
        robotsDirective: "index,follow",
        schemaType: "Article + BreadcrumbList",
        ogTitle: "Trek Near [Location] | Plan Your Route",
        ogDescription: "Route highlights, itinerary suggestions, and local travel planning for this trek.",
        pageGoal: "Rank long-tail trek destination queries and convert to itinerary exploration.",
        contentNotes: "Ensure every page intro and FAQ block is unique at the location level.",
        internalLinkPlan: "Cross-link to related trek pages, nearby travel guides, and required preparation content.",
        backlinkPlan: "Promote standout trek pages through community mentions and roundup features.",
        technicalNotes: "Validate schema and image alt text whenever new trek content is published.",
        nextStep: "Enforce unique intros and FAQs for every trek detail page.",
        sitemapIncluded: true,
    },
    "tour-detail": {
        metaState: "Needs Work",
        focusKeyword: "tour package [destination]",
        secondaryKeywords: "tour itinerary, travel package, [destination] tour plan",
        titleTag: "Tour Package for [Destination] | Itinerary, Highlights and Booking Info",
        metaDescription: "Review itinerary, highlights, and trip planning details for the [destination] tour package.",
        h1Title: "Tour Package for [Destination]",
        canonicalUrl: `${config.clientPath}/tour/[slug]`,
        robotsDirective: "index,follow",
        schemaType: "BreadcrumbList",
        ogTitle: "Tour Package for [Destination]",
        ogDescription: "Tour highlights, itinerary, and booking-ready information for this destination.",
        pageGoal: "Improve package intent rankings and reduce duplication across tour pages.",
        contentNotes: "Tighten title patterns and replace duplicate intros with destination-specific positioning.",
        internalLinkPlan: "Link to related tours, travel guides, and nearby destinations from each package page.",
        backlinkPlan: "Coordinate partner backlinks with branded and destination-based anchor variations.",
        technicalNotes: "Fix schema warnings and review canonical consistency before scaling more tour pages.",
        nextStep: "Standardize title structure and resolve schema validation warnings.",
        sitemapIncluded: true,
    },
    "travel-detail": {
        metaState: "Good",
        focusKeyword: "travel guide [destination]",
        secondaryKeywords: "things to do in [destination], travel planning guide, local attractions",
        titleTag: "Travel Guide for [Destination] | Attractions, Food and Planning Tips",
        metaDescription: "Explore attractions, food, festivals, and planning tips with this destination travel guide.",
        h1Title: "Travel Guide for [Destination]",
        canonicalUrl: `${config.clientPath}/travel/[slug]`,
        robotsDirective: "index,follow",
        schemaType: "Article",
        ogTitle: "Travel Guide for [Destination]",
        ogDescription: "Attractions, local highlights, food, festivals, and planning details for this destination.",
        pageGoal: "Capture informational destination intent and feed users into adjacent content.",
        contentNotes: "Keep local recommendations, food, and festival sections unique per destination.",
        internalLinkPlan: "Link to nearby destinations, event pages, and travel video content from each guide.",
        backlinkPlan: "Promote high-value guides through travel blogs, tourism references, and video embeds.",
        technicalNotes: "Continue improving internal linking and gallery performance across travel detail pages.",
        nextStep: "Expand internal links to nearby travel guides and itineraries.",
        sitemapIncluded: true,
    },
    "upcoming-events": {
        metaState: "Draft",
        focusKeyword: "upcoming travel events",
        secondaryKeywords: "travel events maharashtra, outdoor events, adventure events",
        titleTag: "Upcoming Travel Events | Trek Tour Travel",
        metaDescription: "See upcoming travel, trek, and outdoor events with schedules, highlights, and registration details.",
        h1Title: "Upcoming Travel Events",
        canonicalUrl: `${config.clientPath}/upcoming-events`,
        robotsDirective: "index,follow",
        schemaType: "Event",
        ogTitle: "Upcoming Travel Events",
        ogDescription: "Discover new travel and outdoor events with schedules, speakers, and highlights.",
        pageGoal: "Launch a strong event hub with structured data and event-based discovery.",
        contentNotes: "Build intro copy around seasonal event discovery and keep event summaries scannable.",
        internalLinkPlan: "Link from homepage, trek listing, and travel guides into the events hub.",
        backlinkPlan: "Submit event pages to calendars, local communities, and partner listings.",
        technicalNotes: "Launch with event schema, canonical defaults, and recurring QA for expired events.",
        nextStep: "Launch event schema, speaker fields, and ticketing metadata.",
        sitemapIncluded: true,
    },
    "readers-corner": {
        metaState: "Draft",
        focusKeyword: "travel stories and guides",
        secondaryKeywords: "travel articles, destination stories, travel tips",
        titleTag: "Reader's Corner | Travel Stories, Tips and Editorial Guides",
        metaDescription: "Read travel stories, destination guides, and editorial content from the Trek Tour Travel team.",
        h1Title: "Reader's Corner",
        canonicalUrl: `${config.clientPath}/readers-corner`,
        robotsDirective: "index,follow",
        schemaType: "Article",
        ogTitle: "Reader's Corner",
        ogDescription: "Stories, editorial guides, and destination insights from Trek Tour Travel.",
        pageGoal: "Create an editorial content hub that builds topical authority and internal linking depth.",
        contentNotes: "Define content categories, article templates, author blocks, and FAQ opportunities.",
        internalLinkPlan: "Link editorial stories into destination guides, events, and evergreen category pages.",
        backlinkPlan: "Use digital PR and creator collaborations once the editorial hub launches.",
        technicalNotes: "Prepare article schema, author markup, archive rules, and pagination handling.",
        nextStep: "Define taxonomy, article templates, and article schema defaults.",
        sitemapIncluded: false,
    },
    "archeological-sites": {
        metaState: "Missing",
        focusKeyword: "archeological sites in maharashtra",
        secondaryKeywords: "heritage sites, historical places, culture travel",
        titleTag: "Archeological Sites in Maharashtra | Coming Soon",
        metaDescription: "A future guide to archeological and historical sites in Maharashtra.",
        h1Title: "Archeological Sites in Maharashtra",
        canonicalUrl: `${config.clientPath}/archeological-sites`,
        robotsDirective: "noindex,follow",
        schemaType: "Planned",
        ogTitle: "Archeological Sites in Maharashtra",
        ogDescription: "A planned section for history and heritage destination coverage.",
        pageGoal: "Prepare a future SEO category before the section is launched publicly.",
        contentNotes: "Create the landing page, taxonomy, and template system before indexation.",
        internalLinkPlan: "Map future links from travel guides, reader stories, and destination clusters.",
        backlinkPlan: "Build backlinks only after the section has real content and live pages.",
        technicalNotes: "Keep the route out of sitemap until the launch content is complete.",
        nextStep: "Create the landing page before opening it for indexing.",
        sitemapIncluded: false,
    },
};

const SEO_DETAIL_KEY_BY_PAGE = {
    Home: "home",
    Search: "search",
    "Trek Listing": "trek-listing",
    "Trek Detail Template": "trek-detail",
    "Tour Detail Template": "tour-detail",
    "Travel Detail Template": "travel-detail",
    "Upcoming Events": "upcoming-events",
    "Reader's Corner": "readers-corner",
    "Archeological Sites": "archeological-sites",
};

const getSeoPageDetails = () => {
    if (typeof window === "undefined") {
        return SEO_PAGE_DETAILS_SEED;
    }

    try {
        const stored = window.localStorage.getItem(SEO_PAGE_DETAILS_STORAGE_KEY);
        if (!stored) {
            return SEO_PAGE_DETAILS_SEED;
        }

        const parsed = JSON.parse(stored);
        return Object.fromEntries(
            Object.entries(SEO_PAGE_DETAILS_SEED).map(([key, seed]) => [
                key,
                {
                    ...seed,
                    ...(parsed?.[key] || {}),
                },
            ])
        );
    } catch (error) {
        return SEO_PAGE_DETAILS_SEED;
    }
};

const SeoCMS = () => {
    const navigate = useNavigate();
    const { pageId } = useParams();
    const [activeTab, setActiveTab] = useState("overview");
    const [searchTerm, setSearchTerm] = useState("");
    const [pageTypeFilter, setPageTypeFilter] = useState("All types");
    const [seoPageDetails, setSeoPageDetails] = useState(getSeoPageDetails);

    const deferredSearch = useDeferredValue(normalizeValue(searchTerm));

    const pageInventoryRows = useMemo(
        () =>
            SEO_PAGE_INVENTORY.map((row) => {
                const detail = seoPageDetails[row.id];
                return {
                    ...row,
                    meta: detail?.metaState || row.meta,
                    schema: detail?.schemaType || row.schema,
                    nextStep: detail?.nextStep || row.nextStep,
                    indexing: detail?.robotsDirective?.startsWith("noindex") ? "Noindex" : row.indexing,
                };
            }),
        [seoPageDetails]
    );

    const onPageRows = useMemo(
        () =>
            ON_PAGE_SEO_ROWS.map((row) => {
                const detail = seoPageDetails[SEO_DETAIL_KEY_BY_PAGE[row.page]];
                return {
                    ...row,
                    keyword: detail?.focusKeyword || row.keyword,
                    titleTag: detail?.titleTag || row.titleTag,
                    metaDescription: detail?.metaDescription || row.metaDescription,
                    schema: detail?.schemaType || row.schema,
                };
            }),
        [seoPageDetails]
    );

    const pageTypeOptions = useMemo(
        () => ["All types", ...new Set(pageInventoryRows.map((row) => row.type))],
        [pageInventoryRows]
    );

    const filteredPages = useMemo(
        () =>
            pageInventoryRows.filter((row) => {
                const matchesType = pageTypeFilter === "All types" || row.type === pageTypeFilter;
                return (
                    matchesType &&
                    matchesSearch(deferredSearch, [row.page, row.route, row.type, row.section, row.status, row.nextStep])
                );
            }),
        [deferredSearch, pageInventoryRows, pageTypeFilter]
    );

    const filteredOnPageRows = useMemo(
        () =>
            onPageRows.filter((row) => {
                const matchesType = pageTypeFilter === "All types" || row.type === pageTypeFilter;
                return (
                    matchesType &&
                    matchesSearch(deferredSearch, [
                        row.page,
                        row.type,
                        row.keyword,
                        row.headingStatus,
                        row.internalLinks,
                        row.schema,
                        row.status,
                    ])
                );
            }),
        [deferredSearch, onPageRows, pageTypeFilter]
    );

    const filteredOffPageRows = useMemo(
        () =>
            OFF_PAGE_SEO_ROWS.filter((row) =>
                matchesSearch(deferredSearch, [row.campaign, row.focusPage, row.channel, row.status, row.nextAction])
            ),
        [deferredSearch]
    );

    const filteredTechnicalRows = useMemo(
        () =>
            TECHNICAL_SEO_ROWS.filter((row) =>
                matchesSearch(deferredSearch, [row.area, row.metric, row.current, row.target, row.status, row.owner])
            ),
        [deferredSearch]
    );

    const attentionPages = useMemo(
        () => pageInventoryRows.filter((row) => row.status !== "Healthy").slice(0, 5),
        [pageInventoryRows]
    );

    const selectedPage = useMemo(
        () => pageInventoryRows.find((row) => row.id === pageId) || null,
        [pageId, pageInventoryRows]
    );

    const selectedPageDetails = pageId ? seoPageDetails[pageId] : null;

    const metricCards = useMemo(() => {
        const indexedCount = pageInventoryRows.filter((row) => row.indexing === "Indexed").length;
        const actionCount = pageInventoryRows.filter((row) => row.status !== "Healthy").length;
        const offPageLinks = OFF_PAGE_SEO_ROWS.reduce((sum, row) => sum + row.liveLinks, 0);
        const technicalWatchlist = TECHNICAL_SEO_ROWS.filter((row) =>
            ["Needs Review", "Monitor", "In Progress"].includes(row.status)
        ).length;

        return [
            {
                id: "metric-pages",
                icon: <FiLayers />,
                label: "Tracked page inventory",
                value: pageInventoryRows.length,
                detail: `${indexedCount} pages are index-ready or already indexed.`,
                accent: "blue",
            },
            {
                id: "metric-score",
                icon: <FiTrendingUp />,
                label: "Average SEO health",
                value: `${averageScore(pageInventoryRows)} / 100`,
                detail: `${actionCount} tracked pages still need action or a launch plan.`,
                accent: "green",
            },
            {
                id: "metric-links",
                icon: <FiLink2 />,
                label: "Tracked backlinks",
                value: offPageLinks,
                detail: "Live campaigns are driving referral traffic and authority growth.",
                accent: "amber",
            },
            {
                id: "metric-tech",
                icon: <FiShield />,
                label: "Technical watchlist",
                value: technicalWatchlist,
                detail: "These checks should be reviewed before the next content push.",
                accent: "slate",
            },
        ];
    }, [pageInventoryRows]);

    const toolbarLabel = useMemo(() => {
        if (activeTab === "pages") {
            return `${filteredPages.length} routes in the page inventory`;
        }

        if (activeTab === "on-page") {
            return `${filteredOnPageRows.length} rows in the on-page workspace`;
        }

        if (activeTab === "off-page") {
            return `${filteredOffPageRows.length} rows in the off-page tracker`;
        }

        if (activeTab === "technical") {
            return `${filteredTechnicalRows.length} technical checks in view`;
        }

        return "Search across routes, audits, campaigns, schemas, and technical notes";
    }, [activeTab, filteredOffPageRows.length, filteredOnPageRows.length, filteredPages.length, filteredTechnicalRows.length]);

    const openSitePreview = (path) => {
        window.open(`${config.clientPath}${path}`, "_blank", "noopener,noreferrer");
    };

    const handleEditPage = (id) => {
        setActiveTab("pages");
        navigate(`/cms/seo/${id}`);
    };

    const handleFieldChange = (field, value) => {
        if (!pageId) {
            return;
        }

        setSeoPageDetails((prev) => ({
            ...prev,
            [pageId]: {
                ...prev[pageId],
                [field]: value,
            },
        }));
    };

    const handleToggleChange = (field) => {
        if (!pageId) {
            return;
        }

        setSeoPageDetails((prev) => ({
            ...prev,
            [pageId]: {
                ...prev[pageId],
                [field]: !prev[pageId]?.[field],
            },
        }));
    };

    const handleSavePage = () => {
        if (!pageId) {
            return;
        }

        if (typeof window !== "undefined") {
            window.localStorage.setItem(SEO_PAGE_DETAILS_STORAGE_KEY, JSON.stringify(seoPageDetails));
        }

        showToast("SEO page settings updated successfully.", "success");
    };

    const handleBackToDashboard = () => {
        setActiveTab("pages");
        navigate("/cms/seo");
    };

    const pageColumns = [
        {
            key: "page",
            label: "Page",
            render: (row) => (
                <div className="seoPrimaryCell">
                    <strong>{row.page}</strong>
                    <span>{row.section}</span>
                </div>
            ),
        },
        {
            key: "route",
            label: "Route",
            render: (row) => <code className="seoCodeTag">{row.route}</code>,
        },
        {
            key: "indexing",
            label: "Indexing",
            render: (row) => <StatusPill>{row.indexing}</StatusPill>,
        },
        {
            key: "coverage",
            label: "Meta / Schema",
            render: (row) => (
                <div className="seoStackedCell">
                    <span>{row.meta}</span>
                    <span>{row.schema}</span>
                </div>
            ),
        },
        {
            key: "score",
            label: "Score",
            render: (row) => <ScoreBadge score={row.score} />,
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusPill>{row.status}</StatusPill>,
        },
        {
            key: "nextStep",
            label: "Next Step",
            render: (row) => <span className="seoMutedText">{row.nextStep}</span>,
        },
        {
            key: "preview",
            label: "Preview",
            render: (row) => (
                <button
                    type="button"
                    className={`seoTableButton seoTableButton--ghost ${row.previewPath ? "" : "is-disabled"}`}
                    onClick={() => row.previewPath && openSitePreview(row.previewPath)}
                    disabled={!row.previewPath}
                >
                    {row.previewPath ? "View Live" : "Unavailable"}
                    <FiExternalLink />
                </button>
            ),
        },
        {
            key: "manage",
            label: "Manage",
            render: (row) => (
                <button type="button" className="seoTableButton seoTableButton--primary" onClick={() => handleEditPage(row.id)}>
                    Edit
                    <FiEdit3 />
                </button>
            ),
        },
    ];

    const watchlistColumns = pageColumns.filter((column) => !["preview", "manage"].includes(column.key));

    const onPageColumns = [
        {
            key: "page",
            label: "Page",
            render: (row) => (
                <div className="seoPrimaryCell">
                    <strong>{row.page}</strong>
                    <span>{row.keyword}</span>
                </div>
            ),
        },
        {
            key: "snippet",
            label: "SERP Snippet",
            render: (row) => (
                <div className="seoStackedCell">
                    <span>Title: {row.titleTag}</span>
                    <span>Description: {row.metaDescription}</span>
                </div>
            ),
        },
        {
            key: "headings",
            label: "Headings / Links",
            render: (row) => (
                <div className="seoStackedCell">
                    <span>{row.headingStatus}</span>
                    <span>{row.internalLinks}</span>
                </div>
            ),
        },
        {
            key: "media",
            label: "Media / Schema",
            render: (row) => (
                <div className="seoStackedCell">
                    <span>{row.mediaCoverage}</span>
                    <span>{row.schema}</span>
                </div>
            ),
        },
        {
            key: "score",
            label: "Score",
            render: (row) => <ScoreBadge score={row.score} />,
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusPill>{row.status}</StatusPill>,
        },
    ];

    const offPageColumns = [
        {
            key: "campaign",
            label: "Campaign",
            render: (row) => (
                <div className="seoPrimaryCell">
                    <strong>{row.campaign}</strong>
                    <span>{row.focusPage}</span>
                </div>
            ),
        },
        {
            key: "channel",
            label: "Channel",
            render: (row) => <StatusPill tone="neutral">{row.channel}</StatusPill>,
        },
        {
            key: "authority",
            label: "Authority",
            render: (row) => <span className="seoMutedText">{row.authority}</span>,
        },
        {
            key: "links",
            label: "Link Growth",
            render: (row) => (
                <div className="seoStackedCell">
                    <span>{row.liveLinks} live links</span>
                    <span>{row.referralSessions} referral sessions</span>
                </div>
            ),
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusPill>{row.status}</StatusPill>,
        },
        {
            key: "nextAction",
            label: "Next Action",
            render: (row) => <span className="seoMutedText">{row.nextAction}</span>,
        },
    ];

    const technicalColumns = [
        {
            key: "area",
            label: "Audit Area",
            render: (row) => (
                <div className="seoPrimaryCell">
                    <strong>{row.area}</strong>
                    <span>{row.metric}</span>
                </div>
            ),
        },
        {
            key: "current",
            label: "Current",
            render: (row) => <span className="seoMutedText">{row.current}</span>,
        },
        {
            key: "target",
            label: "Target",
            render: (row) => <span className="seoMutedText">{row.target}</span>,
        },
        {
            key: "impact",
            label: "Impact",
            render: (row) => <span className="seoMutedText">{row.impact}</span>,
        },
        {
            key: "status",
            label: "Status",
            render: (row) => <StatusPill>{row.status}</StatusPill>,
        },
        {
            key: "owner",
            label: "Owner",
            render: (row) => <StatusPill tone="neutral">{row.owner}</StatusPill>,
        },
    ];

    if (pageId) {
        return (
            <>
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>{selectedPage ? `${selectedPage.page} SEO Editor` : "SEO Editor"}</title>
                </Helmet>
                <Nav />
                <div className="row">
                    <MenuBar />
                    <main className="seoWorkspace" style={{ width: "calc(100vw - 240px)" }}>
                        <div className="seoWorkspace__inner">
                            {!selectedPage || !selectedPageDetails ? (
                                <section className="seoEditorShell">
                                    <div className="seoEditorTopbar">
                                        <button type="button" className="seoBackButton" onClick={handleBackToDashboard}>
                                            <FiChevronLeft />
                                            Back to SEO dashboard
                                        </button>
                                    </div>
                                    <article className="seoEditorHero">
                                        <div className="seoEditorHero__copy">
                                            <span className="seoEyebrow">SEO Editor</span>
                                            <h1>Page not found</h1>
                                            <p>The selected SEO page could not be found. Return to the dashboard and choose a valid page entry.</p>
                                        </div>
                                    </article>
                                </section>
                            ) : (
                                <section className="seoEditorShell">
                                    <div className="seoEditorTopbar">
                                        <button type="button" className="seoBackButton" onClick={handleBackToDashboard}>
                                            <FiChevronLeft />
                                            Back to SEO dashboard
                                        </button>
                                        <div className="seoEditorTopbar__actions">
                                            {selectedPage.previewPath ? (
                                                <button
                                                    type="button"
                                                    className="seoTableButton seoTableButton--ghost"
                                                    onClick={() => openSitePreview(selectedPage.previewPath)}
                                                >
                                                    View Live
                                                    <FiExternalLink />
                                                </button>
                                            ) : null}
                                            <button type="button" className="seoTableButton seoTableButton--primary" onClick={handleSavePage}>
                                                Save SEO Details
                                                <FiSave />
                                            </button>
                                        </div>
                                    </div>

                                    <article className="seoEditorHero">
                                        <div className="seoEditorHero__copy">
                                            <span className="seoEyebrow">SEO Editor</span>
                                            <h1>{selectedPage.page}</h1>
                                            <p>{selectedPage.nextStep}</p>
                                            <div className="seoEditorMeta">
                                                <StatusPill tone="neutral">{selectedPage.section}</StatusPill>
                                                <StatusPill>{selectedPage.indexing}</StatusPill>
                                                <StatusPill>{selectedPage.status}</StatusPill>
                                            </div>
                                        </div>
                                        <div className="seoEditorHero__stats">
                                            <div className="seoEditorStat">
                                                <span>Route</span>
                                                <strong>{selectedPage.route}</strong>
                                            </div>
                                            <div className="seoEditorStat">
                                                <span>Score</span>
                                                <strong>{selectedPage.score} / 100</strong>
                                            </div>
                                            <div className="seoEditorStat">
                                                <span>Schema</span>
                                                <strong>{selectedPageDetails.schemaType}</strong>
                                            </div>
                                        </div>
                                    </article>

                                    <div className="seoEditorGrid">
                                        <article className="seoEditorCard">
                                            <div className="seoEditorCard__header">
                                                <span className="seoEditorCard__icon"><FiTarget /></span>
                                                <div>
                                                    <h3>Search Intent</h3>
                                                    <p>Control focus keywords, snippet copy, and the main page promise.</p>
                                                </div>
                                            </div>
                                            <div className="seoEditorFormGrid">
                                                <label className="seoField">
                                                    <span>Focus Keyword</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.focusKeyword}
                                                        onChange={(event) => handleFieldChange("focusKeyword", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField">
                                                    <span>Secondary Keywords</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.secondaryKeywords}
                                                        onChange={(event) => handleFieldChange("secondaryKeywords", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Title Tag</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.titleTag}
                                                        onChange={(event) => handleFieldChange("titleTag", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Meta Description</span>
                                                    <textarea
                                                        rows="3"
                                                        value={selectedPageDetails.metaDescription}
                                                        onChange={(event) => handleFieldChange("metaDescription", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Primary H1</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.h1Title}
                                                        onChange={(event) => handleFieldChange("h1Title", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Page Goal</span>
                                                    <textarea
                                                        rows="3"
                                                        value={selectedPageDetails.pageGoal}
                                                        onChange={(event) => handleFieldChange("pageGoal", event.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </article>

                                        <article className="seoEditorCard">
                                            <div className="seoEditorCard__header">
                                                <span className="seoEditorCard__icon"><FiType /></span>
                                                <div>
                                                    <h3>Indexing and Metadata</h3>
                                                    <p>Manage canonical behavior, robots rules, schema, and metadata state.</p>
                                                </div>
                                            </div>
                                            <div className="seoEditorFormGrid">
                                                <label className="seoField seoField--full">
                                                    <span>Canonical URL</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.canonicalUrl}
                                                        onChange={(event) => handleFieldChange("canonicalUrl", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField">
                                                    <span>Robots Directive</span>
                                                    <select
                                                        value={selectedPageDetails.robotsDirective}
                                                        onChange={(event) => handleFieldChange("robotsDirective", event.target.value)}
                                                    >
                                                        <option value="index,follow">index,follow</option>
                                                        <option value="noindex,follow">noindex,follow</option>
                                                        <option value="noindex,nofollow">noindex,nofollow</option>
                                                    </select>
                                                </label>
                                                <label className="seoField">
                                                    <span>Meta State</span>
                                                    <select
                                                        value={selectedPageDetails.metaState}
                                                        onChange={(event) => handleFieldChange("metaState", event.target.value)}
                                                    >
                                                        <option value="Optimized">Optimized</option>
                                                        <option value="Strong">Strong</option>
                                                        <option value="Good">Good</option>
                                                        <option value="Review">Review</option>
                                                        <option value="Draft">Draft</option>
                                                        <option value="Needs Work">Needs Work</option>
                                                        <option value="Missing">Missing</option>
                                                    </select>
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Schema Type</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.schemaType}
                                                        onChange={(event) => handleFieldChange("schemaType", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoToggleField seoField--full">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPageDetails.sitemapIncluded}
                                                        onChange={() => handleToggleChange("sitemapIncluded")}
                                                    />
                                                    <span>Include this page in the sitemap workflow</span>
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Next Optimization Step</span>
                                                    <textarea
                                                        rows="3"
                                                        value={selectedPageDetails.nextStep}
                                                        onChange={(event) => handleFieldChange("nextStep", event.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </article>

                                        <article className="seoEditorCard">
                                            <div className="seoEditorCard__header">
                                                <span className="seoEditorCard__icon"><FiFileText /></span>
                                                <div>
                                                    <h3>Social and Content Notes</h3>
                                                    <p>Define social preview copy and editorial guidance for this route.</p>
                                                </div>
                                            </div>
                                            <div className="seoEditorFormGrid">
                                                <label className="seoField seoField--full">
                                                    <span>Open Graph Title</span>
                                                    <input
                                                        type="text"
                                                        value={selectedPageDetails.ogTitle}
                                                        onChange={(event) => handleFieldChange("ogTitle", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Open Graph Description</span>
                                                    <textarea
                                                        rows="3"
                                                        value={selectedPageDetails.ogDescription}
                                                        onChange={(event) => handleFieldChange("ogDescription", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Content Notes</span>
                                                    <textarea
                                                        rows="4"
                                                        value={selectedPageDetails.contentNotes}
                                                        onChange={(event) => handleFieldChange("contentNotes", event.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </article>

                                        <article className="seoEditorCard">
                                            <div className="seoEditorCard__header">
                                                <span className="seoEditorCard__icon"><FiLink2 /></span>
                                                <div>
                                                    <h3>Linking and Promotion</h3>
                                                    <p>Capture internal linking plans, off-page opportunities, and technical QA notes.</p>
                                                </div>
                                            </div>
                                            <div className="seoEditorFormGrid">
                                                <label className="seoField seoField--full">
                                                    <span>Internal Link Plan</span>
                                                    <textarea
                                                        rows="4"
                                                        value={selectedPageDetails.internalLinkPlan}
                                                        onChange={(event) => handleFieldChange("internalLinkPlan", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Backlink / Off-Page Plan</span>
                                                    <textarea
                                                        rows="4"
                                                        value={selectedPageDetails.backlinkPlan}
                                                        onChange={(event) => handleFieldChange("backlinkPlan", event.target.value)}
                                                    />
                                                </label>
                                                <label className="seoField seoField--full">
                                                    <span>Technical Notes</span>
                                                    <textarea
                                                        rows="4"
                                                        value={selectedPageDetails.technicalNotes}
                                                        onChange={(event) => handleFieldChange("technicalNotes", event.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </article>
                                    </div>
                                </section>
                            )}
                        </div>
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>SEO Command Center</title>
            </Helmet>
            <Nav />
            <div className="row">
                <MenuBar />
                <main className="seoWorkspace" style={{ width: "calc(100vw - 240px)" }}>
                    <div className="seoWorkspace__inner">
                        <section className="seoHero">
                            <div className="seoHero__copy">
                                <span className="seoEyebrow">Search Visibility Ops</span>
                                <h1>SEO Command Center</h1>
                                <p>
                                    Monitor page readiness, on-page optimization, backlink growth, and technical crawl
                                    health from one professional workspace inside the admin panel.
                                </p>
                                <div className="seoHero__actions">
                                    <button type="button" className="btn-blue" onClick={() => setActiveTab("technical")}>
                                        Run technical review
                                    </button>
                                    <button type="button" className="btn-white" onClick={() => setActiveTab("pages")}>
                                        Review page inventory
                                    </button>
                                </div>
                            </div>
                            <div className="seoHero__meta">
                                <div className="seoHero__metaItem">
                                    <FiRefreshCw />
                                    <div>
                                        <strong>Last workspace sync</strong>
                                        <span>{TODAY_LABEL}</span>
                                    </div>
                                </div>
                                <div className="seoHero__metaItem">
                                    <FiGlobe />
                                    <div>
                                        <strong>Index coverage</strong>
                                        <span>Templates, utility pages, and planned launches are tracked together.</span>
                                    </div>
                                </div>
                                <div className="seoHero__metaItem">
                                    <FiActivity />
                                    <div>
                                        <strong>Technical guardrails</strong>
                                        <span>Sitemap, noindex, canonicals, speed, and schema are on one board.</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="seoMetricGrid">
                            {metricCards.map((card) => (
                                <MetricCard key={card.id} {...card} />
                            ))}
                        </section>

                        <section className="seoTabs">
                            {TAB_ITEMS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`seoTab ${activeTab === tab.key ? "is-active" : ""}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </section>

                        <section className="seoToolbar">
                            <label className="seoSearchField">
                                <FiSearch />
                                <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Search pages, campaigns, schemas, owners, or next steps"
                                />
                            </label>

                            {activeTab !== "off-page" && activeTab !== "technical" ? (
                                <label className="seoSelectField">
                                    <FiFilter />
                                    <select value={pageTypeFilter} onChange={(event) => setPageTypeFilter(event.target.value)}>
                                        {pageTypeOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            ) : null}

                            <div className="seoToolbar__meta">{toolbarLabel}</div>
                        </section>

                        {activeTab === "overview" ? (
                            <section className="seoGrid">
                                <article className="seoPanel seoPanel--span8">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Sections</span>
                                            <h3>SEO workstreams</h3>
                                        </div>
                                        <span className="seoPanel__meta">Use the dashboard sections to move between strategy and execution.</span>
                                    </div>
                                    <div className="seoFocusGrid">
                                        {FOCUS_AREAS.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className="seoFocusCard"
                                                onClick={() => setActiveTab(item.id)}
                                            >
                                                <span className="seoFocusCard__icon">{item.icon}</span>
                                                <strong>{item.title}</strong>
                                                <span>{item.description}</span>
                                                <div className="seoFocusCard__footer">
                                                    <span>{item.stat}</span>
                                                    <span className="seoFocusCard__link">
                                                        Open
                                                        <FiArrowRight />
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </article>

                                <article className="seoPanel seoPanel--span4">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Priority alerts</span>
                                            <h3>What needs attention</h3>
                                        </div>
                                        <FiAlertTriangle className="seoPanel__accentIcon" />
                                    </div>
                                    <div className="seoList">
                                        {SEO_ALERTS.map((alert) => (
                                            <div key={alert.id} className="seoListItem">
                                                <div className="seoListItem__top">
                                                    <StatusPill tone={getPriorityTone(alert.priority)}>{alert.priority}</StatusPill>
                                                    <span className="seoMutedLabel">Open issue</span>
                                                </div>
                                                <strong>{alert.title}</strong>
                                                <p>{alert.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                </article>

                                <article className="seoPanel seoPanel--span6">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Action queue</span>
                                            <h3>Upcoming SEO tasks</h3>
                                        </div>
                                        <FiClock className="seoPanel__accentIcon" />
                                    </div>
                                    <div className="seoList">
                                        {ACTION_QUEUE.map((item) => (
                                            <div key={item.id} className="seoListItem">
                                                <div className="seoListItem__top">
                                                    <strong>{item.title}</strong>
                                                    <StatusPill>{item.status}</StatusPill>
                                                </div>
                                                <p>
                                                    Owner: {item.owner}
                                                    <br />
                                                    Due: {item.due}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </article>

                                <article className="seoPanel seoPanel--span6">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Highlights</span>
                                            <h3>Launch readiness snapshot</h3>
                                        </div>
                                        <FiCheckCircle className="seoPanel__accentIcon" />
                                    </div>
                                    <div className="seoMiniGrid">
                                        {TECHNICAL_HIGHLIGHTS.map((item) => (
                                            <div key={item.id} className="seoMiniCard">
                                                <span>{item.label}</span>
                                                <strong>{item.value}</strong>
                                                <p>{item.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                </article>

                                <article className="seoPanel seoPanel--span12">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Watchlist</span>
                                            <h3>Pages requiring attention</h3>
                                        </div>
                                        <span className="seoPanel__meta">Non-healthy pages are surfaced here for faster weekly review.</span>
                                    </div>
                                    <DataTable columns={watchlistColumns} rows={attentionPages} emptyMessage="No watchlist items right now." />
                                </article>
                            </section>
                        ) : null}

                        {activeTab === "pages" ? (
                            <section className="seoGrid">
                                <article className="seoPanel seoPanel--span12">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Inventory</span>
                                            <h3>All pages and templates</h3>
                                        </div>
                                        <span className="seoPanel__meta">Manage each page, preview live URLs, and jump into focused SEO editing from one table.</span>
                                    </div>
                                    <div className="seoMiniGrid seoMiniGrid--compact">
                                        <div className="seoMiniCard">
                                            <span>Indexed or index-ready</span>
                                            <strong>{pageInventoryRows.filter((row) => row.indexing === "Indexed").length}</strong>
                                            <p>Live routes with stable crawl coverage.</p>
                                        </div>
                                        <div className="seoMiniCard">
                                            <span>Template-led routes</span>
                                            <strong>{pageInventoryRows.filter((row) => row.type === "Template").length}</strong>
                                            <p>These need strict metadata defaults and QA.</p>
                                        </div>
                                        <div className="seoMiniCard">
                                            <span>Planned or backlog</span>
                                            <strong>{pageInventoryRows.filter((row) => ["Planned", "Backlog"].includes(row.status)).length}</strong>
                                            <p>New sections that need launch planning before indexing.</p>
                                        </div>
                                        <div className="seoMiniCard">
                                            <span>Average page score</span>
                                            <strong>{averageScore(filteredPages)} / 100</strong>
                                            <p>Filtered view based on the current search and page type.</p>
                                        </div>
                                    </div>
                                    <DataTable columns={pageColumns} rows={filteredPages} emptyMessage="No pages match the current filters." />
                                </article>
                            </section>
                        ) : null}

                        {activeTab === "on-page" ? (
                            <section className="seoGrid">
                                <article className="seoPanel seoPanel--span12">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Optimization</span>
                                            <h3>On-page SEO workspace</h3>
                                        </div>
                                        <span className="seoPanel__meta">Review snippets, heading structure, internal links, media coverage, and schema readiness.</span>
                                    </div>
                                    <div className="seoMiniGrid seoMiniGrid--compact">
                                        {ON_PAGE_HIGHLIGHTS.map((item) => (
                                            <div key={item.id} className="seoMiniCard">
                                                <span>{item.label}</span>
                                                <strong>{item.value}</strong>
                                                <p>{item.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <DataTable columns={onPageColumns} rows={filteredOnPageRows} emptyMessage="No on-page rows match the current filters." />
                                </article>
                            </section>
                        ) : null}

                        {activeTab === "off-page" ? (
                            <section className="seoGrid">
                                <article className="seoPanel seoPanel--span8">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Growth</span>
                                            <h3>Off-page SEO tracker</h3>
                                        </div>
                                        <span className="seoPanel__meta">Campaigns, partnerships, backlinks, and referral traffic are tracked in one place.</span>
                                    </div>
                                    <div className="seoMiniGrid seoMiniGrid--compact">
                                        {OFF_PAGE_HIGHLIGHTS.map((item) => (
                                            <div key={item.id} className="seoMiniCard">
                                                <span>{item.label}</span>
                                                <strong>{item.value}</strong>
                                                <p>{item.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <DataTable columns={offPageColumns} rows={filteredOffPageRows} emptyMessage="No off-page campaigns match the search query." />
                                </article>

                                <article className="seoPanel seoPanel--span4">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Signal mix</span>
                                            <h3>What to scale next</h3>
                                        </div>
                                        <FiTrendingUp className="seoPanel__accentIcon" />
                                    </div>
                                    <div className="seoList">
                                        <div className="seoListItem">
                                            <div className="seoListItem__top">
                                                <strong>Trek outreach</strong>
                                                <StatusPill>Scaling</StatusPill>
                                            </div>
                                            <p>Guest features and outdoor blogs are sending the highest-quality referral traffic.</p>
                                        </div>
                                        <div className="seoListItem">
                                            <div className="seoListItem__top">
                                                <strong>Brand directories</strong>
                                                <StatusPill>Active</StatusPill>
                                            </div>
                                            <p>These keep citations current and help protect branded query coverage.</p>
                                        </div>
                                        <div className="seoListItem">
                                            <div className="seoListItem__top">
                                                <strong>Reader's Corner PR</strong>
                                                <StatusPill>Planned</StatusPill>
                                            </div>
                                            <p>Keep the digital PR plan ready so the editorial section launches with authority support.</p>
                                        </div>
                                    </div>
                                </article>
                            </section>
                        ) : null}

                        {activeTab === "technical" ? (
                            <section className="seoGrid">
                                <article className="seoPanel seoPanel--span8">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Technical health</span>
                                            <h3>Technical SEO control room</h3>
                                        </div>
                                        <span className="seoPanel__meta">Keep crawl quality, performance, schema, and redirect coverage visible for the full team.</span>
                                    </div>
                                    <div className="seoMiniGrid seoMiniGrid--compact">
                                        {TECHNICAL_HIGHLIGHTS.map((item) => (
                                            <div key={item.id} className="seoMiniCard">
                                                <span>{item.label}</span>
                                                <strong>{item.value}</strong>
                                                <p>{item.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <DataTable columns={technicalColumns} rows={filteredTechnicalRows} emptyMessage="No technical checks match the search query." />
                                </article>

                                <article className="seoPanel seoPanel--span4">
                                    <div className="seoPanel__header">
                                        <div>
                                            <span className="seoPanel__eyebrow">Release checklist</span>
                                            <h3>Before the next launch</h3>
                                        </div>
                                        <FiShield className="seoPanel__accentIcon" />
                                    </div>
                                    <div className="seoChecklist">
                                        <div className="seoChecklist__item">
                                            <FiCheckCircle />
                                            <span>Verify sitemap refresh after publishing new trek, tour, or travel entries.</span>
                                        </div>
                                        <div className="seoChecklist__item">
                                            <FiCheckCircle />
                                            <span>Confirm canonicals and noindex rules on all utility and search states.</span>
                                        </div>
                                        <div className="seoChecklist__item">
                                            <FiAlertTriangle />
                                            <span>Reduce hero image weight to improve mobile LCP before the next campaign launch.</span>
                                        </div>
                                        <div className="seoChecklist__item">
                                            <FiAlertTriangle />
                                            <span>Resolve remaining schema warnings on the tour template and new events page.</span>
                                        </div>
                                    </div>
                                </article>
                            </section>
                        ) : null}
                    </div>
                </main>
            </div>
        </>
    );
};

export default SeoCMS;
